import { TranslateService } from 'Public_old/src/app/Common/Services/translate.service';
import { Helper } from '../Helpers/helper';
import {
	Component,
	Output,
	Input,
	EventEmitter,
	Directive,
	Provider,
	forwardRef,
	ElementRef,
	ViewChild,
	Injector,
	EnvironmentInjector,
	Self,
	inject,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, NgControl, UntypedFormControl } from '@angular/forms';
import { GlobalValidator } from '../Classes/globalValidator';

@Component({
	standalone: false,
	selector: 'visiba-timepicker',
	template: `
		<div class="wallInput" [class.hasIcon]="icon != null" [class.twoFields]="view.length == 4">
			<ng-template [ngIf]="icon != null">
				<visiba-svg class="iconSvg" [a]="icon"></visiba-svg>
			</ng-template>
			<div class="timeField hour" *ngIf="view.toLowerCase().indexOf('hh') > -1">
				<div class="wallStyling">
					<input
						[tabindex]="tabInd"
						type="text"
						[(ngModel)]="hourModel"
						[readonly]="readonly"
						[maxLength]="2"
						(ngModelChange)="onModelChange()"
						[placeholder]="localPlaceholderHours"
						(focus)="showList('hour')"
						(click)="showList('hour')"
						(keypress)="keyPress($event)"
						(blur)="blur($event, 'hour')"
						(keydown.arrowdown)="arrowMove(1)"
						(keydown.arrowup)="arrowMove(-1)"
						(keyup.esc)="hideList('hour')"
					/>
				</div>
				<div #hourListEl class="list" [hidden]="!hourListVisible">
					<ul>
						<ng-template ngFor let-d [ngForOf]="hourTimeList">
							<li
								(click)="setValue('hour', d)"
								[class.selected]="hourModel == d || '0' + hourModel == d"
								[class.targeted]="d === hourTargetedValue"
								(mouseover)="hourTargetedValue = d"
							>
								{{ d }}
							</li>
						</ng-template>
					</ul>
				</div>
			</div>
			<div class="timeSeperator" *ngIf="view.length == 4">:</div>
			<div class="timeField minute" *ngIf="view.indexOf('mm') > -1">
				<div class="wallStyling">
					<input
						[tabindex]="tabInd"
						#minuteInput
						type="text"
						[(ngModel)]="minuteModel"
						[readonly]="readonly"
						[maxLength]="2"
						(ngModelChange)="onModelChange()"
						[placeholder]="localPlaceholderMinutes"
						(focus)="showList('minute')"
						(click)="showList('minute')"
						(keypress)="keyPress($event)"
						(blur)="blur($event, 'minute')"
						(keydown.arrowdown)="arrowMove(1)"
						(keydown.arrowup)="arrowMove(-1)"
						(keyup.esc)="hideList('minute')"
					/>
				</div>
				<div #minuteListEl class="list" [hidden]="!minuteListVisible">
					<ul>
						<ng-template ngFor let-d [ngForOf]="minuteTimeList">
							<li
								(click)="setValue('minute', d)"
								[class.selected]="minuteModel == d || '0' + minuteModel == d"
								[class.targeted]="d === minuteTargetedValue"
								(mouseover)="minuteTargetedValue = d"
							>
								{{ d }}
							</li>
						</ng-template>
					</ul>
				</div>
			</div>
			<label class="value" [class.svg]="icon != null">{{ localPlaceholder }}</label>
			<span class="error" [class.value]="model != null">{{ currentError }}</span>
		</div>
	`,
	host: {
		'[class.readonly]': 'readonly',
	},
})
export class VisibaTimepickerComponent implements ControlValueAccessor {
	@Input() tabInd: number = 0;
	@Input() icon: string = null;
	@Input() placeholder: string = '';
	@Input() view: string = 'hhmm'; // HHmm, HH, mm, hhmm, hh
	@Input('modelValue') model: Date = null;
	@Output() modelChange: any;
	@Input() readonly: boolean = false;
	@Input() errorMessages: { [name: string]: string } | string = 'default';

	@ViewChild('minuteListEl', { static: false }) minuteListEl: ElementRef;
	@ViewChild('hourListEl', { static: false }) hourListEl: ElementRef;
	@ViewChild('minuteInput', { static: false }) minuteInputEl: ElementRef;

	hourTimeList: Array<string> = [];
	hourListVisible: boolean = false;
	hourTargetedValue: string = 'dummy';

	minuteTimeList: Array<string> = [];
	minuteListVisible: boolean = false;
	minuteTargetedValue: string = 'dummy';

	currentError: string = null;
	hourModel: string = null;
	minuteModel: string = null;
	localPlaceholder: string = null;
	localPlaceholderHours: string = null;
	localPlaceholderMinutes: string = null;

	constructor(
		public elem: ElementRef,
		@Self() public controlDir: NgControl,
		private helper: Helper,
		private translateService: TranslateService,
	) {
		controlDir.valueAccessor = this;

		this.modelChange = new EventEmitter();
	}

	ngOnInit() {
		for (let i = 0; i < (this.view.indexOf('HH') > -1 ? 24 : 12); i++) {
			this.hourTimeList.push(this.helper.number.padZero(i).toString());
		}

		for (let j = 0; j < 60; j += 5) {
			this.minuteTimeList.push(this.helper.number.padZero(j).toString());
		}

		if (this.placeholder !== null && this.placeholder !== '') {
			this.localPlaceholder = this.translateService.get(this.placeholder);
		}

		this.localPlaceholderHours = this.translateService.get('Common.Abbreviation.Hours24');
		this.localPlaceholderMinutes = this.translateService.get('Common.Abbreviation.Minutes');
	}

	writeValue(value: Date) {
		window.setTimeout(() => {
			this.handleErrors();
		});

		if (typeof value !== 'undefined') {
			this.model = value;

			this.hourModel = value === null ? null : value.format(this.view.indexOf('hh') > -1 ? 'hh' : 'HH');
			this.minuteModel = value === null ? null : value.format('mm');

			this.hourTargetedValue = this.hourModel;
			this.minuteTargetedValue = this.minuteModel;
		} else this.model = null;
	}

	onModelChange() {
		const hour = String.isNullOrEmpty(this.hourModel) ? 0 : parseInt(this.hourModel);
		const minute = String.isNullOrEmpty(this.minuteModel) ? 0 : parseInt(this.minuteModel);

		const combValue = hour.toString().pad(2, '0') + ':' + minute.toString().pad(2, '0');

		if (
			(this.view.toLowerCase().indexOf('hh') > -1 && String.isNullOrEmpty(this.hourModel)) ||
			(this.view.indexOf('mm') > -1 && String.isNullOrEmpty(this.minuteModel))
		) {
			this.model = null;
		} else {
			this.model = new Date(new Date().setHours(hour, minute, 0, 0));
		}

		this.emitValue(this.model);

		this.hourTargetedValue = String.isNullOrEmpty(this.hourModel) ? null : this.hourModel.pad(2, '0');
		this.minuteTargetedValue = String.isNullOrEmpty(this.minuteModel) ? null : this.minuteModel.pad(2, '0');
	}

	// Call to push value
	propagateChange = (_: any) => {};

	registerOnChange(fn) {
		this.propagateChange = fn;
	}

	registerOnTouch() {}
	registerOnTouched() {}

	emitValue(value: any) {
		this.hideList('hour');
		this.hideList('minute');
		this.model = value;
		this.modelChange.emit(value);
		this.propagateChange(value);
		this.handleErrors();
	}

	setValue(type: 'hour' | 'minute', value: any) {
		this[type + 'Model'] = value;

		this.onModelChange();

		if (this.view.length === 4 && type === 'hour' && (this.minuteModel === null || this.minuteModel === '')) {
			this.minuteInputEl.nativeElement.focus();
		}
	}

	showList(type: 'hour' | 'minute') {
		const el = this[type + 'ListEl'].nativeElement;

		if (this.readonly || this[type + 'ListVisible']) return;

		this.hourListVisible = false;
		this.minuteListVisible = false;

		this[type + 'ListVisible'] = true;

		window.setTimeout(() => {
			const doc = document.getElementsByTagName('html')[0];
			doc.addEventListener('click', this.listClickCheck, false);
			if (el.querySelector('.targeted') !== null) {
				el.scrollTop = el.querySelector('.targeted').offsetTop - 60;
			} else if (this.view.indexOf('HH') > -1) {
				el.scrollTop = 175;
			} else {
				el.scrollTop = 0;
			}
		});
	}

	hideList(type: 'hour' | 'minute') {
		this[type + 'ListVisible'] = false;
		const doc = document.getElementsByTagName('html')[0];
		doc.removeEventListener('click', this.listClickCheck, false);
	}

	blur(e: any, type: 'hour' | 'minute') {
		if (e.relatedTarget !== null) {
			// Needed to be able to select option with mouse click as the blur event is triggered on click outside.
			this.hideList(type);
		}
	}

	keyPress(event: KeyboardEvent) {
		if (event.keyCode === 46 || event.keyCode === 58 || (event.keyCode >= 48 && event.keyCode <= 57)) {
			return;
		}

		event.stopPropagation();
		event.preventDefault();

		if (event.type === 'keydown') return;

		const type: 'hour' | 'minute' = this.minuteInputEl.nativeElement !== document.activeElement ? 'hour' : 'minute';

		switch (event.key) {
			case 'Enter':
				if (this[type + 'ListVisible']) {
					this.setValue(type, this[type + 'TargetedValue']);
				}
				break;
			case 'Escape':
				this.hideList(type);
				break;
		}
	}

	listClickCheck = (event) => {
		if (
			this.hourListVisible &&
			event.target &&
			this.elem.nativeElement !== event.target &&
			!this.elem.nativeElement.contains(event.target)
		) {
			this.hideList('hour');
		}
		if (
			this.minuteListVisible &&
			event.target &&
			this.elem.nativeElement !== event.target &&
			!this.elem.nativeElement.contains(event.target)
		) {
			this.hideList('minute');
		}
	};

	arrowMove(direction: number) {
		if (this.hourListVisible === false && this.minuteListVisible === false) {
			const type: 'hour' | 'minute' = this.minuteInputEl.nativeElement !== document.activeElement ? 'hour' : 'minute';
			this.showList(type);
		}

		const type: 'hour' | 'minute' = this.hourListVisible ? 'hour' : 'minute';

		let pos = -1;
		if (this[type + 'TargetedValue'] !== null) pos = this[type + 'TimeList'].indexOf(this[type + 'TargetedValue']);

		const newPos = pos + direction;

		if (newPos >= 0 && newPos < this[type + 'TimeList'].length) this[type + 'TargetedValue'] = this[type + 'TimeList'][newPos];

		const targetElement = this[type + 'ListEl'].nativeElement.querySelector('.targeted');
		if (targetElement !== null) this[type + 'ListEl'].nativeElement.scrollTop = targetElement.offsetTop - targetElement.clientHeight;
	}

	handleErrors() {
		this.currentError = GlobalValidator.validateFormControl(
			this.translateService,
			this.controlDir.control as UntypedFormControl,
			this.errorMessages,
		);
	}
}
